import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFulfillmentsApi, adminOrdersApi } from '../api/sales';
import { adminInventoryApi, adminShippingApi, adminKolayGelsinApi } from '../api/operations';
import type {
  CancelOrderDto,
  CreateRefundDto,
  FulfillmentAllocation,
  FulfillmentDto,
  OrderCancelReason,
  PaymentMethodType,
  ReturnDisposition,
} from '../api/types';
import { orderCancelReasonValues, paymentMethodTypeValues } from '../api/types';
import { PageHeader } from '../components/Shell';
import {
  Badge,
  ConfirmDialog,
  ErrorState,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Spinner,
} from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, money, number } from '../lib/format';
import {
  financialStatusLabels,
  fulfillmentStatusLabels,
  labelOf,
  orderStatusLabels,
  paymentMethodLabels,
  refundStatusLabels,
  shipmentStatusLabels,
} from '../lib/labels';
import { P } from '../lib/permissions';

const cancelReasonLabels: Record<OrderCancelReason, string> = {
  CustomerRequest: 'Müşteri talebi',
  Fraud: 'Şüpheli işlem',
  InventoryUnavailable: 'Stok yok',
  PaymentDeclined: 'Ödeme reddedildi',
  Duplicate: 'Mükerrer sipariş',
  Other: 'Diğer',
};

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const orderId = Number(id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [fulfillmentOpen, setFulfillmentOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const order = useQuery({
    queryKey: ['admin', 'order', orderId],
    queryFn: () => adminOrdersApi.getById(orderId),
    enabled: Number.isFinite(orderId),
  });

  const refunds = useQuery({
    queryKey: ['admin', 'order', orderId, 'refunds'],
    queryFn: () => adminOrdersApi.refunds(orderId),
    enabled: Number.isFinite(orderId),
  });

  const plan = useQuery({
    queryKey: ['admin', 'order', orderId, 'plan'],
    queryFn: () => adminOrdersApi.fulfillmentPlan(orderId),
    enabled: Number.isFinite(orderId) && fulfillmentOpen,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
  };

  const archive = useMutation({
    mutationFn: () => adminOrdersApi.archive(orderId),
    onSuccess: (message) => {
      toast.success(message ?? 'Sipariş arşivlendi.');
      setArchiveOpen(false);
      invalidate();
    },
    onError: (error) => toast.fromError(error, 'Arşivleme başarısız.'),
  });

  const completeRefund = useMutation({
    mutationFn: (refundId: number) =>
      adminOrdersApi.completeRefund(refundId, 'Panelden elle tamamlandı'),
    onSuccess: () => {
      toast.success('İade tamamlandı olarak işaretlendi.');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId, 'refunds'] });
    },
    onError: (error) => toast.fromError(error, 'İade tamamlanamadı.'),
  });

  if (order.isLoading) return <PageLoader />;
  if (order.isError) return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;

  const data = order.data!;

  return (
    <div>
      <PageHeader
        title={data.orderNumber}
        description={`${formatDateTime(data.createdAt)} · ${data.channel}`}
        actions={
          <>
            <Link to="/siparisler" className="btn-secondary">
              Listeye dön
            </Link>
            {can(P.orders.update) ? (
              <button type="button" className="btn-secondary" onClick={() => setNotesOpen(true)}>
                Not ve etiket
              </button>
            ) : null}
            {can(P.orders.update) && data.outstandingTotal > 0 ? (
              <button type="button" className="btn-secondary" onClick={() => setPaymentOpen(true)}>
                Ödeme kaydet
              </button>
            ) : null}
            {can(P.orders.fulfill) && data.fulfillmentStatus !== 'Fulfilled' ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setFulfillmentOpen(true)}
              >
                Sevkiyat oluştur
              </button>
            ) : null}
          </>
        }
      />

      <div className="mb-5 flex flex-wrap gap-2">
        <Badge label={labelOf(orderStatusLabels, data.status)} />
        <Badge label={labelOf(financialStatusLabels, data.financialStatus)} />
        <Badge label={labelOf(fulfillmentStatusLabels, data.fulfillmentStatus)} />
        {data.riskScore !== null && data.riskScore !== undefined && data.riskScore >= 40 ? (
          <span className="badge bg-red-50 text-red-700">Risk skoru {data.riskScore}</span>
        ) : null}
        {data.tags
          ? data.tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
              .map((tag) => (
                <span key={tag} className="badge bg-ink-100 text-ink-700">
                  {tag}
                </span>
              ))
          : null}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          {/* Kalemler */}
          <section className="card overflow-hidden">
            <h2 className="border-b border-ink-200 px-5 py-3.5 text-sm font-semibold text-ink-900">
              Ürünler ({data.lines.length})
            </h2>
            <div className="scroll-x">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-ink-200 bg-ink-50/70 text-left text-xs uppercase tracking-wide text-ink-500">
                    <th className="px-4 py-2.5 font-semibold">Ürün</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Adet</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Sevk</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Birim</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Maliyet</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50">
                            {line.imageUrl ? (
                              <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                            ) : null}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-ink-900">{line.productTitle}</p>
                            <p className="truncate text-xs text-ink-400">
                              {line.variantTitle ? `${line.variantTitle} · ` : ''}
                              {line.sku}
                            </p>
                            {line.refundedQuantity > 0 ? (
                              <p className="text-xs text-amber-700">
                                {line.refundedQuantity} adet iade
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">{number(line.quantity)}</td>
                      <td className="px-4 py-3 text-right text-ink-500">
                        {number(line.fulfilledQuantity)}
                      </td>
                      <td className="px-4 py-3 text-right">{money(line.unitPrice, data.currencyCode)}</td>
                      <td className="px-4 py-3 text-right text-ink-500">
                        {line.unitCost ? money(line.unitCost, data.currencyCode) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {money(line.lineTotal, data.currencyCode)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Sevkiyatlar */}
          {data.fulfillments.length > 0 ? (
            <section className="card p-5">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Sevkiyatlar</h2>
              <ul className="space-y-4">
                {data.fulfillments.map((fulfillment) => (
                  <FulfillmentCard
                    key={fulfillment.id}
                    fulfillment={fulfillment}
                    onChanged={invalidate}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {/* Odeme hareketleri */}
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Ödeme hareketleri</h2>
            {data.transactions.length === 0 ? (
              <p className="text-sm text-ink-500">Henüz ödeme hareketi yok.</p>
            ) : (
              <div className="scroll-x">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                      <th className="pb-2 font-semibold">Tarih</th>
                      <th className="pb-2 font-semibold">Tür</th>
                      <th className="pb-2 font-semibold">Yöntem</th>
                      <th className="pb-2 font-semibold">Durum</th>
                      <th className="pb-2 text-right font-semibold">Tutar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {data.transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="py-2 text-ink-600">
                          {formatDateTime(transaction.processedAt ?? transaction.createdAt)}
                        </td>
                        <td className="py-2 text-ink-800">{transaction.kind}</td>
                        <td className="py-2 text-ink-600">
                          {paymentMethodLabels[transaction.methodType] ?? transaction.methodType}
                          {transaction.cardLastFour ? ` ••${transaction.cardLastFour}` : ''}
                        </td>
                        <td className="py-2">
                          <span
                            className={`badge ${
                              transaction.status === 'Success'
                                ? 'bg-emerald-50 text-emerald-700'
                                : transaction.status === 'Failure'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-ink-100 text-ink-700'
                            }`}
                          >
                            {transaction.status}
                          </span>
                        </td>
                        <td className="py-2 text-right font-medium">
                          {money(transaction.amount, transaction.currencyCode)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Iadeler */}
          <section className="card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-900">İadeler</h2>
              {can(P.orders.refund) && data.paidTotal > data.refundedTotal ? (
                <button type="button" className="btn-secondary btn-sm" onClick={() => setRefundOpen(true)}>
                  İade oluştur
                </button>
              ) : null}
            </div>

            {(refunds.data ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">Bu siparişte iade kaydı yok.</p>
            ) : (
              <ul className="space-y-3">
                {(refunds.data ?? []).map((refund) => (
                  <li
                    key={refund.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-200 p-3.5"
                  >
                    <div>
                      <p className="text-sm font-medium text-ink-900">
                        {refund.refundNumber} · {money(refund.totalAmount, refund.currencyCode)}
                      </p>
                      <p className="text-xs text-ink-500">
                        {formatDateTime(refund.createdAt)}
                        {refund.reason ? ` · ${refund.reason}` : ''}
                        {refund.issuedAsStoreCredit ? ' · mağaza kredisi' : ''}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge label={labelOf(refundStatusLabels, refund.status)} />
                      {refund.status === 'Pending' && can(P.orders.refund) ? (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => completeRefund.mutate(refund.id)}
                          disabled={completeRefund.isPending}
                          title="Para gönderildikten sonra iadeyi tamamlandı olarak işaretler"
                        >
                          Tamamlandı işaretle
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {(refunds.data ?? []).some((refund) => refund.status === 'Pending') ? (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Havale veya kapıda ödemede otomatik iade yapılamaz. Parayı gönderdikten sonra
                iadeyi tamamlandı olarak işaretleyin; sipariş toplamları o anda güncellenir.
              </p>
            ) : null}
          </section>

          {/* Zaman cizelgesi */}
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Zaman çizelgesi</h2>
            <ol className="space-y-3 border-l border-ink-200 pl-4">
              {data.timeline.map((event) => (
                <li key={event.id} className="relative text-sm">
                  <span
                    className={`absolute -left-[21px] top-1.5 h-2 w-2 rounded-full ${
                      event.isSystemGenerated ? 'bg-ink-300' : 'bg-brand-500'
                    }`}
                  />
                  <p className="text-ink-700">{event.message}</p>
                  <p className="text-xs text-ink-400">
                    {formatDateTime(event.createdAt)}
                    {event.performedByName ? ` · ${event.performedByName}` : ''}
                    <span className="ml-1 font-mono text-[10px] text-ink-300">{event.eventType}</span>
                  </p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Yan panel */}
        <aside className="space-y-5">
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Tutarlar</h2>
            <dl className="space-y-2 text-sm">
              <Row label="Ara toplam" value={money(data.subTotal, data.currencyCode)} />
              {data.discountTotal > 0 ? (
                <Row label="İndirim" value={`− ${money(data.discountTotal, data.currencyCode)}`} />
              ) : null}
              <Row label="Kargo" value={money(data.shippingTotal, data.currencyCode)} />
              {data.taxLines.map((tax) => (
                <Row
                  key={tax.title}
                  label={`${tax.title} (%${tax.rate})`}
                  value={money(tax.amount, data.currencyCode)}
                />
              ))}
              {data.giftCardTotal > 0 ? (
                <Row label="Hediye kartı" value={`− ${money(data.giftCardTotal, data.currencyCode)}`} />
              ) : null}
              <div className="flex justify-between border-t border-ink-200 pt-2 font-semibold text-ink-900">
                <dt>Toplam</dt>
                <dd>{money(data.grandTotal, data.currencyCode)}</dd>
              </div>
              <Row label="Ödenen" value={money(data.paidTotal, data.currencyCode)} />
              {data.refundedTotal > 0 ? (
                <Row label="İade edilen" value={money(data.refundedTotal, data.currencyCode)} />
              ) : null}
              {data.outstandingTotal > 0 ? (
                <div className="flex justify-between font-medium text-amber-700">
                  <dt>Kalan</dt>
                  <dd>{money(data.outstandingTotal, data.currencyCode)}</dd>
                </div>
              ) : null}
            </dl>
          </section>

          <section className="card p-5">
            <h2 className="mb-3 text-sm font-semibold text-ink-900">Müşteri</h2>
            <p className="text-sm font-medium text-ink-900">{data.customerFullName ?? '—'}</p>
            <p className="text-sm text-ink-600">{data.email}</p>
            {data.phone ? <p className="text-sm text-ink-600">{data.phone}</p> : null}
            {data.userId ? (
              <Link to={`/musteriler/${data.userId}`} className="link mt-2 inline-block text-sm">
                Müşteri kartını aç
              </Link>
            ) : (
              <p className="mt-2 text-xs text-ink-400">Misafir sipariş</p>
            )}
          </section>

          {data.shippingAddress ? (
            <section className="card p-5">
              <h2 className="mb-3 text-sm font-semibold text-ink-900">Teslimat adresi</h2>
              <address className="text-sm not-italic leading-6 text-ink-600">
                {data.shippingAddress.firstName} {data.shippingAddress.lastName}
                <br />
                {data.shippingAddress.addressLine1}
                {data.shippingAddress.addressLine2 ? (
                  <>
                    <br />
                    {data.shippingAddress.addressLine2}
                  </>
                ) : null}
                <br />
                {data.shippingAddress.district} / {data.shippingAddress.city}
                <br />
                {data.shippingAddress.countryName} · {data.shippingAddress.phone}
              </address>
              {data.shippingAddress.deliveryNote ? (
                <p className="mt-2 rounded bg-ink-50 px-2.5 py-1.5 text-xs text-ink-600">
                  Not: {data.shippingAddress.deliveryNote}
                </p>
              ) : null}
            </section>
          ) : null}

          {data.billingAddress ? (
            <section className="card p-5">
              <h2 className="mb-3 text-sm font-semibold text-ink-900">Fatura adresi</h2>
              <address className="text-sm not-italic leading-6 text-ink-600">
                {data.billingAddress.firstName} {data.billingAddress.lastName}
                <br />
                {data.billingAddress.addressLine1}
                <br />
                {data.billingAddress.district} / {data.billingAddress.city}
              </address>
              {data.billingAddress.isCorporate ? (
                <p className="mt-2 text-xs text-ink-500">
                  {data.billingAddress.taxOffice} · {data.billingAddress.taxNumber}
                </p>
              ) : null}
            </section>
          ) : null}

          {data.customerNote || data.staffNote ? (
            <section className="card p-5">
              <h2 className="mb-3 text-sm font-semibold text-ink-900">Notlar</h2>
              {data.customerNote ? (
                <div className="mb-3">
                  <p className="text-xs font-medium text-ink-500">Müşteri notu</p>
                  <p className="mt-0.5 text-sm text-ink-700">{data.customerNote}</p>
                </div>
              ) : null}
              {data.staffNote ? (
                <div>
                  <p className="text-xs font-medium text-ink-500">Personel notu</p>
                  <p className="mt-0.5 text-sm text-ink-700">{data.staffNote}</p>
                </div>
              ) : null}
            </section>
          ) : null}

          <section className="card p-5">
            <h2 className="mb-3 text-sm font-semibold text-ink-900">İşlemler</h2>
            <div className="flex flex-col gap-2">
              {can(P.orders.cancel) && data.status !== 'Cancelled' ? (
                <button type="button" className="btn-secondary" onClick={() => setCancelOpen(true)}>
                  Siparişi iptal et
                </button>
              ) : null}
              {can(P.orders.update) && data.status !== 'Archived' ? (
                <button type="button" className="btn-secondary" onClick={() => setArchiveOpen(true)}>
                  Arşivle
                </button>
              ) : null}
            </div>
          </section>
        </aside>
      </div>

      <PaymentModal
        open={paymentOpen}
        orderId={orderId}
        outstanding={data.outstandingTotal}
        currency={data.currencyCode}
        onClose={() => setPaymentOpen(false)}
        onDone={invalidate}
      />

      <FulfillmentModal
        open={fulfillmentOpen}
        orderId={orderId}
        lines={data.lines.map((line) => ({
          id: line.id,
          title: line.productTitle,
          variantTitle: line.variantTitle ?? null,
          sku: line.sku,
          fulfillable: line.fulfillableQuantity,
        }))}
        plan={plan.data ?? []}
        onClose={() => setFulfillmentOpen(false)}
        onDone={invalidate}
      />

      <RefundModal
        open={refundOpen}
        orderId={orderId}
        currency={data.currencyCode}
        lines={data.lines.map((line) => ({
          id: line.id,
          title: line.productTitle,
          variantTitle: line.variantTitle ?? null,
          refundable: line.quantity - line.refundedQuantity - line.cancelledQuantity,
          unitPrice: line.unitPrice,
        }))}
        onClose={() => setRefundOpen(false)}
        onDone={() => {
          invalidate();
          void queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId, 'refunds'] });
        }}
      />

      <CancelModal
        open={cancelOpen}
        orderId={orderId}
        isPaid={data.paidTotal > 0}
        onClose={() => setCancelOpen(false)}
        onDone={invalidate}
      />

      <NotesModal
        open={notesOpen}
        orderId={orderId}
        staffNote={data.staffNote ?? ''}
        tags={data.tags ?? ''}
        onClose={() => setNotesOpen(false)}
        onDone={invalidate}
      />

      <ConfirmDialog
        open={archiveOpen}
        title="Siparişi arşivle"
        message="Sipariş aktif listeden çıkar; kayıt silinmez."
        confirmLabel="Arşivle"
        busy={archive.isPending}
        onCancel={() => setArchiveOpen(false)}
        onConfirm={() => archive.mutate()}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-600">{label}</dt>
      <dd className="text-ink-900">{value}</dd>
    </div>
  );
}

/* ---------------------------- Sevkiyat karti ---------------------------- */

function FulfillmentCard({
  fulfillment,
  onChanged,
}: {
  fulfillment: FulfillmentDto;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { can } = useAuth();
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState(fulfillment.trackingNumber ?? '');
  const [deliveredTo, setDeliveredTo] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);

  const carriers = useQuery({
    queryKey: ['admin', 'carriers'],
    queryFn: () => adminShippingApi.carriers(),
    enabled: trackingOpen,
    staleTime: 5 * 60_000,
  });

  const [carrierId, setCarrierId] = useState<number | null>(null);

  const updateTracking = useMutation({
    mutationFn: () =>
      adminFulfillmentsApi.updateTracking(fulfillment.id, {
        trackingNumber: trackingNumber || null,
        carrierId,
        notifyCustomer: true,
      }),
    onSuccess: () => {
      toast.success('Takip bilgisi güncellendi.');
      setTrackingOpen(false);
      onChanged();
    },
  });

  const markDelivered = useMutation({
    mutationFn: () => adminFulfillmentsApi.markDelivered(fulfillment.id, deliveredTo || undefined),
    onSuccess: (message) => {
      toast.success(message ?? 'Teslim edildi olarak işaretlendi.');
      onChanged();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const cancel = useMutation({
    mutationFn: () => adminFulfillmentsApi.cancel(fulfillment.id, 'Personel iptali'),
    onSuccess: (message) => {
      toast.success(message ?? 'Sevkiyat iptal edildi.');
      setCancelOpen(false);
      onChanged();
    },
    onError: (error) => toast.fromError(error, 'İptal edilemedi.'),
  });

  const dispatchKolayGelsin = useMutation({
    mutationFn: () => adminKolayGelsinApi.dispatch(fulfillment.id),
    onSuccess: (data) => {
      toast.success(`Kolay Gelsin sevk emri oluşturuldu. Takip No: ${data.trackingNumber}`);
      onChanged();
    },
    onError: (error) => toast.fromError(error, 'Kolay Gelsin sevk emri oluşturulamadı.'),
  });

  return (
    <li className="rounded-lg border border-ink-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-ink-900">
            {fulfillment.fulfillmentNumber}
            {fulfillment.carrierName ? ` · ${fulfillment.carrierName}` : ' · Kolay Gelsin'}
          </p>
          <p className="text-xs text-ink-500">
            {fulfillment.locationName ?? '—'}
            {fulfillment.trackingNumber ? ` · Takip No: ${fulfillment.trackingNumber}` : ''}
          </p>
          {fulfillment.trackingUrl ? (
            <a
              href={fulfillment.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="link text-xs block mt-1"
            >
              Kolay Gelsin ile Takip Et ↗
            </a>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge label={labelOf(shipmentStatusLabels, fulfillment.trackingStatus)} />
          {can(P.orders.fulfill) ? (
            <>
              {!fulfillment.trackingNumber && fulfillment.state !== 'Cancelled' ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm text-brand-700 font-semibold"
                  onClick={() => dispatchKolayGelsin.mutate()}
                  disabled={dispatchKolayGelsin.isPending}
                >
                  {dispatchKolayGelsin.isPending ? <Spinner className="h-3 w-3 mr-1" /> : null}
                  Kolay Gelsin'e Gönder
                </button>
              ) : null}
              {fulfillment.trackingNumber ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm text-xs font-semibold text-ink-800"
                  onClick={() => {
                    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'https://api.hurmahane.com').replace(/\/+$/, '');
                    const labelUrl = fulfillment.shippingLabelUrl
                      ? (fulfillment.shippingLabelUrl.startsWith('http')
                          ? fulfillment.shippingLabelUrl
                          : `${apiBase}${fulfillment.shippingLabelUrl.startsWith('/') ? '' : '/'}${fulfillment.shippingLabelUrl}`)
                      : `${apiBase}/api/admin/shipping/kolay-gelsin/fulfillments/${fulfillment.id}/label`;
                    window.open(labelUrl, '_blank', 'width=850,height=900');
                  }}
                  title="Kargo barkod etiketini aç ve yazdır"
                >
                  🖨️ Barkod Yazdır
                </button>
              ) : null}
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setTrackingOpen(true)}
              >
                Takip
              </button>
              {fulfillment.trackingStatus !== 'Delivered' ? (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => markDelivered.mutate()}
                  disabled={markDelivered.isPending}
                >
                  Teslim edildi
                </button>
              ) : null}
              {fulfillment.state !== 'Cancelled' ? (
                <button
                  type="button"
                  className="btn-ghost btn-sm text-red-600"
                  onClick={() => setCancelOpen(true)}
                >
                  İptal
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <ul className="mt-3 space-y-1 text-sm text-ink-600">
        {fulfillment.lines.map((line) => (
          <li key={line.orderLineId}>
            {line.productTitle} — {line.quantity} adet
          </li>
        ))}
      </ul>

      {fulfillment.trackingEvents.length > 0 ? (
        <ol className="mt-3 space-y-1.5 border-l border-ink-200 pl-4">
          {fulfillment.trackingEvents.map((event) => (
            <li key={`${event.occurredAt}-${event.status}`} className="relative text-xs">
              <span className="absolute -left-[19px] top-1.5 h-1.5 w-1.5 rounded-full bg-ink-300" />
              <span className="text-ink-700">{event.description ?? event.status}</span>
              <span className="ml-2 text-ink-400">{formatDateTime(event.occurredAt)}</span>
            </li>
          ))}
        </ol>
      ) : null}

      <Modal
        open={trackingOpen}
        title="Takip bilgisi"
        onClose={() => setTrackingOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setTrackingOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => updateTracking.mutate()}
              disabled={updateTracking.isPending}
            >
              {updateTracking.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={updateTracking.error} />

          <Field label="Kargo firması" htmlFor="ff-carrier">
            <select
              id="ff-carrier"
              className="field"
              value={carrierId ?? ''}
              onChange={(event) =>
                setCarrierId(event.target.value ? Number(event.target.value) : null)
              }
            >
              <option value="">Değiştirme</option>
              {(carriers.data ?? []).map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Takip numarası" htmlFor="ff-tracking">
            <input
              id="ff-tracking"
              className="field font-mono text-sm"
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
            />
          </Field>

          <Field label="Teslim alan" htmlFor="ff-delivered">
            <input
              id="ff-delivered"
              className="field"
              placeholder="Teslim edildi işaretlerken kullanılır"
              value={deliveredTo}
              onChange={(event) => setDeliveredTo(event.target.value)}
            />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelOpen}
        title="Sevkiyatı iptal et"
        message="Sevkiyat iptal edilir ve ürünler stoğa geri alınır."
        confirmLabel="İptal et"
        danger
        busy={cancel.isPending}
        onCancel={() => setCancelOpen(false)}
        onConfirm={() => cancel.mutate()}
      />
    </li>
  );
}

/* ------------------------------ Odeme kaydi ------------------------------ */

function PaymentModal({
  open,
  orderId,
  outstanding,
  currency,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  outstanding: number;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState<PaymentMethodType>('BankTransfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () =>
      adminOrdersApi.recordPayment(orderId, {
        amount,
        methodType: method,
        referenceNumber: reference || null,
        note: note || null,
      }),
    onSuccess: (message) => {
      toast.success(message ?? 'Ödeme kaydedildi.');
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title="Ödeme kaydet"
      description={`Kalan tutar: ${money(outstanding, currency)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || amount <= 0}
          >
            {save.isPending ? <Spinner className="h-4 w-4" /> : null}
            Kaydet
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={save.error} />

        <Field label="Tutar" htmlFor="pay-amount" required>
          <input
            id="pay-amount"
            type="number"
            step="0.01"
            min={0}
            className="field"
            value={amount}
            onChange={(event) => setAmount(Number(event.target.value))}
          />
        </Field>

        <Field label="Ödeme yöntemi" htmlFor="pay-method" required>
          <select
            id="pay-method"
            className="field"
            value={method}
            onChange={(event) => setMethod(event.target.value as PaymentMethodType)}
          >
            {paymentMethodTypeValues.map((value) => (
              <option key={value} value={value}>
                {paymentMethodLabels[value]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Referans numarası" htmlFor="pay-ref" hint="Dekont veya işlem numarası">
          <input
            id="pay-ref"
            className="field"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>

        <Field label="Not" htmlFor="pay-note">
          <textarea
            id="pay-note"
            className="field min-h-20"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ---------------------------- Sevkiyat olustur ---------------------------- */

interface FulfillableLine {
  id: number;
  title: string;
  variantTitle: string | null;
  sku: string;
  fulfillable: number;
}

function FulfillmentModal({
  open,
  orderId,
  lines,
  plan,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  lines: FulfillableLine[];
  plan: FulfillmentAllocation[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [locationId, setLocationId] = useState<number | null>(null);
  const [carrierId, setCarrierId] = useState<number | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notify, setNotify] = useState(true);

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => adminInventoryApi.locations(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const carriers = useQuery({
    queryKey: ['admin', 'carriers'],
    queryFn: () => adminShippingApi.carriers(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const selected = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([lineId, quantity]) => ({ orderLineId: Number(lineId), quantity }));

  const activeLocation = locationId ?? locations.data?.[0]?.id ?? null;

  const create = useMutation({
    mutationFn: () =>
      adminOrdersApi.createFulfillment(orderId, {
        lines: selected,
        // Kullanici secim yapmadiysa listedeki ilk konum kullanilir.
        locationId: activeLocation!,
        carrierId,
        trackingNumber: trackingNumber || null,
        notifyCustomer: notify,
      }),
    onSuccess: () => {
      toast.success('Sevkiyat oluşturuldu.');
      setQuantities({});
      setTrackingNumber('');
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title="Sevkiyat oluştur"
      description="Sevk edilecek kalemleri ve adetlerini seçin."
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || selected.length === 0 || activeLocation === null}
          >
            {create.isPending ? <Spinner className="h-4 w-4" /> : null}
            Sevkiyatı oluştur
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={create.error} />

        {plan.length > 0 ? (
          <div className="rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-900">
            <p className="font-medium">Önerilen karşılama planı</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {plan.map((allocation, index) => (
                <li key={index}>
                  {allocation.locationName}: {allocation.quantity} adet
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
          {lines.map((line) => (
            <li key={line.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-900">{line.title}</p>
                <p className="truncate text-xs text-ink-500">
                  {line.variantTitle ? `${line.variantTitle} · ` : ''}
                  {line.sku} · sevk edilebilir {line.fulfillable}
                </p>
              </div>
              <input
                type="number"
                min={0}
                max={line.fulfillable}
                disabled={line.fulfillable <= 0}
                className="field w-20 py-1.5 text-center text-sm"
                value={quantities[line.id] ?? 0}
                onChange={(event) =>
                  setQuantities((current) => ({
                    ...current,
                    [line.id]: Math.max(
                      0,
                      Math.min(Number(event.target.value) || 0, line.fulfillable),
                    ),
                  }))
                }
                aria-label={`${line.title} sevk adedi`}
              />
            </li>
          ))}
        </ul>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Çıkış konumu" htmlFor="ff-location" required>
            <select
              id="ff-location"
              className="field"
              value={activeLocation ?? ''}
              onChange={(event) => setLocationId(Number(event.target.value))}
            >
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Kargo firması" htmlFor="ff-new-carrier">
            <select
              id="ff-new-carrier"
              className="field"
              value={carrierId ?? ''}
              onChange={(event) =>
                setCarrierId(event.target.value ? Number(event.target.value) : null)
              }
            >
              <option value="">Seçilmedi</option>
              {(carriers.data ?? []).map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Takip numarası" htmlFor="ff-new-tracking">
          <input
            id="ff-new-tracking"
            className="field font-mono text-sm"
            value={trackingNumber}
            onChange={(event) => setTrackingNumber(event.target.value)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={notify}
            onChange={(event) => setNotify(event.target.checked)}
          />
          Müşteriye kargo bildirimi gönder
        </label>
      </div>
    </Modal>
  );
}

/* ------------------------------ Iade olustur ------------------------------ */

interface RefundableLine {
  id: number;
  title: string;
  variantTitle: string | null;
  refundable: number;
  unitPrice: number;
}

function RefundModal({
  open,
  orderId,
  currency,
  lines,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  currency: string;
  lines: RefundableLine[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [restock, setRestock] = useState(true);
  const [disposition, setDisposition] = useState<ReturnDisposition>('Restock');
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [refundShipping, setRefundShipping] = useState(false);
  const [storeCredit, setStoreCredit] = useState(false);
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);

  const selected = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([lineId, quantity]) => ({
      orderLineId: Number(lineId),
      quantity,
      restock,
      disposition,
    }));

  const estimated =
    selected.reduce((total, line) => {
      const source = lines.find((item) => item.id === line.orderLineId);
      return total + (source ? source.unitPrice * line.quantity : 0);
    }, 0) + additionalAmount;

  const create = useMutation({
    mutationFn: () => {
      const dto: CreateRefundDto = {
        lines: selected,
        additionalAmount,
        refundShipping,
        issueAsStoreCredit: storeCredit,
        reason: reason || null,
        notifyCustomer: notify,
      };
      return adminOrdersApi.createRefund(orderId, dto);
    },
    onSuccess: (refund) => {
      toast.success(
        refund.status === 'Pending'
          ? 'İade kaydedildi; para gönderildikten sonra tamamlandı işaretleyin.'
          : 'İade işlendi.',
      );
      setQuantities({});
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title="İade oluştur"
      description="İade edilecek kalemleri seçin."
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || (selected.length === 0 && additionalAmount <= 0)}
          >
            {create.isPending ? <Spinner className="h-4 w-4" /> : null}
            İadeyi oluştur
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={create.error} />

        <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
          {lines.map((line) => (
            <li key={line.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-900">{line.title}</p>
                <p className="truncate text-xs text-ink-500">
                  {line.variantTitle ? `${line.variantTitle} · ` : ''}
                  {money(line.unitPrice, currency)} · iade edilebilir {line.refundable}
                </p>
              </div>
              <input
                type="number"
                min={0}
                max={line.refundable}
                disabled={line.refundable <= 0}
                className="field w-20 py-1.5 text-center text-sm"
                value={quantities[line.id] ?? 0}
                onChange={(event) =>
                  setQuantities((current) => ({
                    ...current,
                    [line.id]: Math.max(0, Math.min(Number(event.target.value) || 0, line.refundable)),
                  }))
                }
                aria-label={`${line.title} iade adedi`}
              />
            </li>
          ))}
        </ul>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ek iade tutarı" htmlFor="rf-extra" hint="Jest indirimi vb.">
            <input
              id="rf-extra"
              type="number"
              step="0.01"
              min={0}
              className="field"
              value={additionalAmount}
              onChange={(event) => setAdditionalAmount(Number(event.target.value))}
            />
          </Field>

          <Field label="Ürün durumu" htmlFor="rf-disposition">
            <select
              id="rf-disposition"
              className="field"
              value={disposition}
              onChange={(event) => setDisposition(event.target.value as ReturnDisposition)}
            >
              <option value="Restock">Stoğa geri al</option>
              <option value="Damaged">Hasarlı</option>
              <option value="Discard">İmha</option>
              <option value="ReturnToSupplier">Tedarikçiye iade</option>
            </select>
          </Field>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={restock}
              onChange={(event) => setRestock(event.target.checked)}
            />
            Ürünleri stoğa geri al
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={refundShipping}
              onChange={(event) => setRefundShipping(event.target.checked)}
            />
            Kargo bedelini de iade et
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={storeCredit}
              onChange={(event) => setStoreCredit(event.target.checked)}
            />
            Karta iade yerine mağaza kredisi ver
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={notify}
              onChange={(event) => setNotify(event.target.checked)}
            />
            Müşteriye bilgilendirme gönder
          </label>
        </div>

        <Field label="Gerekçe" htmlFor="rf-reason">
          <input
            id="rf-reason"
            className="field"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <p className="rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-700">
          Tahmini iade tutarı: <strong>{money(estimated, currency)}</strong>
        </p>
      </div>
    </Modal>
  );
}

/* ------------------------------ Siparis iptal ------------------------------ */

function CancelModal({
  open,
  orderId,
  isPaid,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  isPaid: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<CancelOrderDto>({
    reason: 'CustomerRequest',
    note: '',
    restockItems: true,
    refundPayment: isPaid,
    notifyCustomer: true,
  });

  const cancel = useMutation({
    mutationFn: () => adminOrdersApi.cancel(orderId, form),
    onSuccess: (message) => {
      toast.success(message ?? 'Sipariş iptal edildi.');
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title="Siparişi iptal et"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
          >
            {cancel.isPending ? <Spinner className="h-4 w-4" /> : null}
            Siparişi iptal et
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={cancel.error} />

        <Field label="İptal nedeni" htmlFor="cancel-reason" required>
          <select
            id="cancel-reason"
            className="field"
            value={form.reason}
            onChange={(event) =>
              setForm((c) => ({ ...c, reason: event.target.value as OrderCancelReason }))
            }
          >
            {orderCancelReasonValues.map((value) => (
              <option key={value} value={value}>
                {cancelReasonLabels[value] ?? value}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Not" htmlFor="cancel-note">
          <textarea
            id="cancel-note"
            className="field min-h-20"
            value={form.note ?? ''}
            onChange={(event) => setForm((c) => ({ ...c, note: event.target.value }))}
          />
        </Field>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={form.restockItems}
              onChange={(event) => setForm((c) => ({ ...c, restockItems: event.target.checked }))}
            />
            Ürünleri stoğa geri al
          </label>
          {isPaid ? (
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={form.refundPayment}
                onChange={(event) => setForm((c) => ({ ...c, refundPayment: event.target.checked }))}
              />
              Alınan ödemeyi iade et
            </label>
          ) : null}
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={form.notifyCustomer}
              onChange={(event) => setForm((c) => ({ ...c, notifyCustomer: event.target.checked }))}
            />
            Müşteriye bilgilendirme gönder
          </label>
        </div>
      </div>
    </Modal>
  );
}

/* --------------------------------- Notlar --------------------------------- */

function NotesModal({
  open,
  orderId,
  staffNote,
  tags,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  staffNote: string;
  tags: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [note, setNote] = useState(staffNote);
  const [tagValue, setTagValue] = useState(tags);

  const save = useMutation({
    mutationFn: () =>
      adminOrdersApi.updateNotes(orderId, { staffNote: note || null, tags: tagValue || null }),
    onSuccess: (message) => {
      toast.success(message ?? 'Kaydedildi.');
      onDone();
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title="Not ve etiket"
      description="Personel notu müşteriye gösterilmez."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? <Spinner className="h-4 w-4" /> : null}
            Kaydet
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormErrors error={save.error} />

        <Field label="Personel notu" htmlFor="note-staff">
          <textarea
            id="note-staff"
            className="field min-h-28"
            maxLength={2000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        <Field label="Etiketler" htmlFor="note-tags" hint="Virgülle ayırın.">
          <input
            id="note-tags"
            className="field"
            value={tagValue}
            onChange={(event) => setTagValue(event.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
